import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import ProgressTrackerLinks from "./ProgressTrackerLinks";
import "../styles/JobApplications.css";

interface JobApplication {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  status: "applied" | "interview_scheduled" | "rejected" | "offered" | "accepted";
  appliedAt: string;
  interviewDate: string | null;
  notes: string;
  url: string;
  // Enhanced fields
  extractedSkills?: string[];
  extractedTools?: string[];
  experienceRequired?: string;
  companyIndustry?: string;
  metadataExtracted?: boolean;
  resumeUsedId?: string;
  coverLetterUsedId?: string;
  updatedVia?: "manual" | "email-sync"; // Track how status was updated
  lastStatusUpdateAt?: string; // Track when status was last updated
}

interface Stats {
  totalApplications: number;
  totalSavedJobs: number;
  applicationsByStatus: {
    applied: number;
    interview_scheduled: number;
    rejected: number;
    offered: number;
    accepted: number;
  };
  successRate: string;
}

const FILTER_OPTIONS = [
  { value: "", label: "All", icon: "📊" },
  { value: "applied", label: "Waiting", icon: "⏳" },
  { value: "interview_scheduled", label: "Interview", icon: "📅" },
  { value: "offered", label: "Offers", icon: "🎁" },
  { value: "accepted", label: "Accepted", icon: "✅" },
  { value: "rejected", label: "Unsuccessful Attempt", icon: "❌" }
];

const JobApplications: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<string>("");
  const [editingDate, setEditingDate] = useState<string>("");
  const [editingNotes, setEditingNotes] = useState<string>("");
  const [editingAddress, setEditingAddress] = useState<string>("");
  const [editingResumeId, setEditingResumeId] = useState<string>("");
  const [editingRequiredSkills, setEditingRequiredSkills] = useState<string>("");
  const [availableResumes, setAvailableResumes] = useState<{id: string; fileName: string}[]>([]);
  const [focusedFilterIndex, setFocusedFilterIndex] = useState<number>(0);
  // Subscription & premium features
  const [isPremium, setIsPremium] = useState(false);
  const [hoveredNotes, setHoveredNotes] = useState<string | null>(null);
  // Progress Tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [trackerApp, setTrackerApp] = useState<JobApplication | null>(null);
  // AI Opportunity Analysis modal
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [analyzeModal, setAnalyzeModal] = useState<any>(null);
  const [applyingInsights, setApplyingInsights] = useState(false);

  // Keyboard navigation handler for filter buttons
  const handleFilterKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, filterIndex: number) => {
    let newIndex = filterIndex;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        newIndex = (filterIndex + 1) % FILTER_OPTIONS.length;
        setFocusedFilterIndex(newIndex);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        newIndex = (filterIndex - 1 + FILTER_OPTIONS.length) % FILTER_OPTIONS.length;
        setFocusedFilterIndex(newIndex);
        break;
      case "Home":
        e.preventDefault();
        setFocusedFilterIndex(0);
        break;
      case "End":
        e.preventDefault();
        setFocusedFilterIndex(FILTER_OPTIONS.length - 1);
        break;
      default:
        return;
    }
  }, []);

  // Focus effect for keyboard navigation
  useEffect(() => {
    const filterBtn = document.getElementById(`filter-btn-${focusedFilterIndex}`) as HTMLButtonElement;
    if (filterBtn) {
      filterBtn.focus();
    }
  }, [focusedFilterIndex]);

  useEffect(() => {
    const startTime = performance.now();
    
    const fetchApplications = async () => {
      try {
        setLoading(true);
        const queryParams = statusFilter ? `?status=${statusFilter}` : "";
        const response = await axiosInstance.get(`/auth/jobs/applications${queryParams}`);
        setApplications(response.data.applications);
        setError(null);
        
        // Log render time for performance monitoring
        const endTime = performance.now();
        console.debug(`Filter render time: ${(endTime - startTime).toFixed(2)}ms`);
      } catch (err: any) {
        setError(err.response?.data?.error || "Failed to load applications");
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get("/auth/jobs/stats");
        setStats(response.data);
      } catch (err) {
        console.error("Failed to load stats:", err);
      }
    };

    fetchApplications();
    fetchStats();

    // Fetch subscription status for premium features
    const fetchSubscription = async () => {
      try {
        const response = await axiosInstance.get("/auth/profile");
        setIsPremium(response.data.subscription?.isActive || false);
      } catch (err) {
        console.error("Failed to fetch subscription status:", err);
        setIsPremium(false);
      }
    };
    fetchSubscription();

    // Fetch resumes for resume-used selector
    axiosInstance.get("/auth/resumes").then(r => {
      setAvailableResumes(r.data.resumes || []);
    }).catch(() => {});
  }, [statusFilter]);

  const handleStatusUpdate = async (appId: string) => {
    if (!editingStatus) {
      toast.error("Please select a status");
      return;
    }

    try {
      const data: any = { status: editingStatus };
      if (editingNotes) data.notes = editingNotes;
      if (editingDate && editingStatus === "interview_scheduled") {
        data.interviewDate = editingDate;
      }
      if (editingAddress) data.applicationAddress = editingAddress;
      if (editingResumeId) data.resumeUsedId = editingResumeId;
      if (editingRequiredSkills) data.requiredSkills = editingRequiredSkills;

      await axiosInstance.put(`/auth/jobs/applications/${appId}`, data);
      setEditingId(null);
      toast.success("Application updated successfully!");
      
      // Refetch applications and stats
      const response = await axiosInstance.get(`/auth/jobs/applications${statusFilter ? `?status=${statusFilter}` : ""}`);
      setApplications(response.data.applications);
      const statsResponse = await axiosInstance.get("/auth/jobs/stats");
      setStats(statsResponse.data);

      // Show progress tracker for certain statuses
      const updatedApp = response.data.applications.find((app: JobApplication) => app.id === appId);
      if (updatedApp && ["interview_scheduled", "accepted", "rejected"].includes(editingStatus)) {
        setTrackerApp(updatedApp);
        setShowProgressTracker(true);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to update application");
    }
  };

  const handleAnalyzeOpportunity = async (app: JobApplication) => {
    setAnalyzingId(app.id);
    try {
      const res = await axiosInstance.post(`/auth/jobs/applications/${app.id}/analyze`);
      setAnalyzeModal({ ...res.data, app });
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to analyze opportunity");
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleApplyInsights = async () => {
    if (!analyzeModal) return;
    setApplyingInsights(true);
    try {
      await axiosInstance.patch(`/auth/jobs/applications/${analyzeModal.applicationId}/enrich`, {
        jobDescription: `${analyzeModal.jobTitle} at ${analyzeModal.company}\n\nRequired skills: ${(analyzeModal.skills || []).join(", ")}`,
      });
      setApplications(prev =>
        prev.map(a =>
          a.id === analyzeModal.applicationId
            ? { ...a, extractedSkills: analyzeModal.skills, extractedTools: analyzeModal.tools, metadataExtracted: true }
            : a
        )
      );
      toast.success("AI insights saved to your application record");
      setAnalyzeModal(null);
    } catch {
      toast.error("Failed to save insights");
    } finally {
      setApplyingInsights(false);
    }
  };

  const handleDeleteApplication = async (appId: string) => {
    if (!window.confirm("Remove this application from your tracker?")) return;

    try {
      await axiosInstance.delete(`/auth/jobs/applications/${appId}`);
      setApplications(applications.filter((app) => app.id !== appId));
      toast.success("Application removed");
      // Refetch stats
      const statsResponse = await axiosInstance.get("/auth/jobs/stats");
      setStats(statsResponse.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to remove application");
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "applied":
        return "waiting";
      case "interview_scheduled":
        return "interview";
      case "rejected":
        return "closed";
      case "offered":
        return "offered";
      case "accepted":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      applied: "Waiting for Response",
      interview_scheduled: "Interview Scheduled",
      rejected: "Unsuccessful Attempt",
      offered: "Offer Received",
      accepted: "Accepted"
    };
    return labels[status] || status.replace(/_/g, " ");
  };

  return (
    <div className="applications-container">
      <button onClick={() => navigate("/dashboard")} className="hb-btn hb-btn-outline" style={{ marginBottom: 'var(--spacing-lg)' }}>
        ← Back to Dashboard
      </button>

      {/* Coach Message */}
      <div className="hb-coach-message" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <span className="hb-coach-icon">📋</span>
        <p>Every application is progress. Keep tracking your journey!</p>
      </div>

      <div className="applications-header">
        <h1>📋 Your Job Applications</h1>
        <button 
          className="kanban-switch-btn"
          onClick={() => navigate("/applications/kanban")}
        >
          ▦ Kanban Board
        </button>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.totalApplications}</div>
            <div className="stat-label">Total Applications</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.applicationsByStatus.applied}</div>
            <div className="stat-label">Waiting</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.applicationsByStatus.interview_scheduled}</div>
            <div className="stat-label">Interviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.applicationsByStatus.offered}</div>
            <div className="stat-label">Offers</div>
          </div>
          <div className="stat-card success">
            <div className="stat-number">{stats.applicationsByStatus.accepted}</div>
            <div className="stat-label">Accepted</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-number">{stats.successRate}%</div>
            <div className="stat-label">Success Rate</div>
          </div>
        </div>
      )}

      <div className="filter-section" role="group" aria-label="Filter applications by status">
        <label id="filter-label">Filter by Status:</label>
        <div className="filter-buttons-wrapper" role="toolbar" aria-labelledby="filter-label">
          {FILTER_OPTIONS.map((option, index) => (
            <button
              key={option.value}
              id={`filter-btn-${index}`}
              className={`filter-button ${statusFilter === option.value ? "active" : ""}`}
              onClick={() => setStatusFilter(option.value)}
              onKeyDown={(e) => handleFilterKeyDown(e, index)}
              aria-pressed={statusFilter === option.value}
              aria-label={`${option.label} applications`}
              title={`Filter: ${option.label}`}
              tabIndex={index === focusedFilterIndex ? 0 : -1}
            >
              <span className="filter-btn-icon">{option.icon}</span>
              <span className="filter-btn-label">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="loading">Loading your applications...</p>}
      {error && <div className="hb-alert hb-alert-error">{error}</div>}

      {!loading && applications.length === 0 && (
        <div className="hb-recommendations-empty">
          <div className="hb-recommendations-empty-icon">🎯</div>
          <h3 className="hb-recommendations-empty-title">No applications tracked yet</h3>
          <p className="hb-recommendations-empty-text">
            Start applying to jobs and track your progress here!
          </p>
        </div>
      )}

      {!loading && applications.length > 0 && (
        <div className="applications-list">
          {applications.map((app) => (
            <div key={app.id} className="app-card">
              {editingId === app.id ? (
                <div className="edit-form">
                  <h4>{app.jobTitle}</h4>
                  <p className="company">{app.company} • {app.location}</p>

                  <div className="form-group">
                    <label>Status:</label>
                    <select
                      value={editingStatus}
                      onChange={(e) => setEditingStatus(e.target.value)}
                      className={`hb-input status-select status-select-${getStatusClass(editingStatus)}`}
                    >
                      <option value="">Select status</option>
                      <option value="applied">Waiting for Response</option>
                      <option value="interview_scheduled">Interview Scheduled</option>
                      <option value="rejected">Unsuccessful Attempt</option>
                      <option value="offered">Offer Received</option>
                      <option value="accepted">Accepted</option>
                    </select>
                  </div>

                  {editingStatus === "interview_scheduled" && (
                    <div className="form-group">
                      <label>Interview Date:</label>
                      <input
                        type="datetime-local"
                        value={editingDate}
                        onChange={(e) => setEditingDate(e.target.value)}
                        className="hb-input"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Notes:</label>
                    <textarea
                      value={editingNotes}
                      onChange={(e) => setEditingNotes(e.target.value)}
                      placeholder="Track your thoughts, questions, or follow-ups..."
                      rows={3}
                      className="hb-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Applied Via (email / URL):</label>
                    <input
                      type="text"
                      value={editingAddress}
                      onChange={(e) => setEditingAddress(e.target.value)}
                      placeholder="e.g. careers@company.com or application URL"
                      className="hb-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Resume Used:</label>
                    <select
                      value={editingResumeId}
                      onChange={(e) => setEditingResumeId(e.target.value)}
                      className="hb-input"
                    >
                      <option value="">— Not specified —</option>
                      {availableResumes.map(r => (
                        <option key={r.id} value={r.id}>{r.fileName}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Required Skills (comma-separated):</label>
                    <input
                      type="text"
                      value={editingRequiredSkills}
                      onChange={(e) => setEditingRequiredSkills(e.target.value)}
                      placeholder="e.g. React, TypeScript, Node.js"
                      className="hb-input"
                    />
                  </div>

                  <div className="edit-actions">
                    <button
                      className="hb-btn hb-btn-primary"
                      onClick={() => handleStatusUpdate(app.id)}
                    >
                      Save
                    </button>
                    <button
                      className="hb-btn hb-btn-outline"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="app-card-header">
                    <div className="app-card-logo">
                      {app.company.charAt(0).toUpperCase()}
                    </div>
                    <div className="app-card-info">
                      <h3 className="app-card-role">{app.jobTitle}</h3>
                      <p className="app-card-company">{app.company}</p>
                    </div>
                    <div className={`app-card-status hb-badge-${getStatusClass(editingId === app.id && editingStatus ? editingStatus : app.status)}`}>
                      {getStatusLabel(editingId === app.id && editingStatus ? editingStatus : app.status)}
                      {editingId === app.id && editingStatus && editingStatus !== app.status && (
                        <span style={{ fontSize: "0.7rem", marginLeft: "4px", opacity: 0.7 }}>*</span>
                      )}
                    </div>
                  </div>

                  <div className="app-content">
                    <div className="app-location">📍 {app.location}</div>
                    <div className="app-last-activity">
                      <span className="app-meta-icon">📅</span>
                      <span>Applied {new Date(app.appliedAt).toLocaleDateString()}</span>
                    </div>

                    {app.interviewDate && (
                      <div className="app-meta">
                        <span className="app-meta-icon">🎯</span>
                        <span>Interview: {new Date(app.interviewDate).toLocaleString()}</span>
                      </div>
                    )}

                    {/* Experience Required */}
                    {(app as any).experienceRequired && (
                      <div className="app-meta">
                        <span className="app-meta-icon">⏱️</span>
                        <span>{(app as any).experienceRequired}</span>
                      </div>
                    )}

                    {/* Company Industry */}
                    {app.companyIndustry && (
                      <div className="app-meta">
                        <span className="app-meta-icon">🏢</span>
                        <span>{app.companyIndustry}</span>
                      </div>
                    )}

                    {/* Extracted Skills (Premium) */}
                    {app.extractedSkills && app.extractedSkills.length > 0 && (
                      <div className="app-skills-section">
                        <span className="app-skills-label">
                          💡 Skills Required
                          {!isPremium && <span className="app-premium-badge" title="Premium feature">👑</span>}
                        </span>
                        <div className="app-skills-container">
                          {app.extractedSkills.slice(0, 5).map((skill, idx) => (
                            <span key={idx} className="app-skill-tag">
                              {skill}
                            </span>
                          ))}
                          {app.extractedSkills.length > 5 && (
                            <span className="app-skill-more">+{app.extractedSkills.length - 5} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Notes with Hover Tooltip */}
                    {app.notes && (
                      <div className="app-notes-wrapper">
                        <div 
                          className="app-meta app-notes-preview"
                          onMouseEnter={() => setHoveredNotes(app.id)}
                          onMouseLeave={() => setHoveredNotes(null)}
                        >
                          <span className="app-meta-icon">📝</span>
                          <span className="app-notes-truncated">
                            {app.notes.length > 60 ? `${app.notes.substring(0, 60)}...` : app.notes}
                          </span>
                        </div>
                        {hoveredNotes === app.id && app.notes.length > 60 && (
                          <div className="app-notes-tooltip">
                            <div className="tooltip-arrow"></div>
                            <p>{app.notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      className="hb-link"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "inherit" }}
                      onClick={() => handleAnalyzeOpportunity(app)}
                      disabled={analyzingId === app.id}
                    >
                      {analyzingId === app.id ? "Analyzing..." : "View Opportunity"}
                    </button>
                  </div>

                  <div className="app-card-actions">
                    <button
                      className="hb-btn hb-btn-primary"
                      onClick={() => {
                        setEditingId(app.id);
                        setEditingStatus(app.status);
                        setEditingDate(app.interviewDate ? app.interviewDate.substring(0, 16) : "");
                        setEditingNotes(app.notes || "");
                        setEditingAddress((app as any).applicationAddress || "");
                        setEditingResumeId((app as any).resumeUsedId || "");
                        setEditingRequiredSkills(((app as any).extractedSkills || []).join(", "));
                      }}
                    >
                      ✏️ Update
                    </button>
                    <button
                      className="hb-btn hb-btn-secondary"
                      onClick={() => handleDeleteApplication(app.id)}
                    >
                      Remove
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* AI Opportunity Analysis Modal */}
      {analyzeModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
          onClick={() => setAnalyzeModal(null)}>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", maxWidth: "600px", width: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#1e293b" }}>{analyzeModal.jobTitle}</h2>
                <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.9rem" }}>{analyzeModal.company}</p>
                {analyzeModal.fromStoredData && <span style={{ fontSize: "0.72rem", background: "#f0fdf4", color: "#15803d", padding: "2px 8px", borderRadius: "20px", marginTop: "6px", display: "inline-block" }}>Saved at application time</span>}
                {!analyzeModal.fromStoredData && analyzeModal.llmPowered && <span style={{ fontSize: "0.72rem", background: "#eff6ff", color: "#2563eb", padding: "2px 8px", borderRadius: "20px", marginTop: "6px", display: "inline-block" }}>AI-powered analysis</span>}
                {!analyzeModal.fromStoredData && !analyzeModal.llmPowered && <span style={{ fontSize: "0.72rem", background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: "20px", marginTop: "6px", display: "inline-block" }}>Heuristic analysis</span>}
              </div>
              <button onClick={() => setAnalyzeModal(null)} style={{ background: "none", border: "none", fontSize: "1.4rem", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>✕</button>
            </div>

            {/* Match Score */}
            {analyzeModal.hasResume && (
              <div style={{ display: "flex", alignItems: "center", gap: "16px", background: "#f8fafc", borderRadius: "12px", padding: "16px 20px", marginBottom: "20px" }}>
                <div style={{ textAlign: "center", minWidth: "64px" }}>
                  <div style={{ fontSize: "2rem", fontWeight: 700, color: analyzeModal.matchScore >= 70 ? "#10b981" : analyzeModal.matchScore >= 45 ? "#f59e0b" : "#ef4444" }}>
                    {analyzeModal.matchScore}%
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: "2px" }}>Match</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden", marginBottom: "8px" }}>
                    <div style={{ height: "100%", width: `${analyzeModal.matchScore}%`, background: analyzeModal.matchScore >= 70 ? "#10b981" : analyzeModal.matchScore >= 45 ? "#f59e0b" : "#ef4444", borderRadius: "4px", transition: "width 0.6s ease" }} />
                  </div>
                  {analyzeModal.rationale && <p style={{ margin: 0, fontSize: "0.85rem", color: "#475569", lineHeight: 1.5 }}>{analyzeModal.rationale}</p>}
                </div>
              </div>
            )}
            {!analyzeModal.hasResume && (
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", fontSize: "0.85rem", color: "#92400e" }}>
                Upload a resume in your Profile to get a personalised match score.
              </div>
            )}

            {/* Matched Skills */}
            {analyzeModal.matchedSkills?.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h4 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: "#15803d", textTransform: "uppercase", letterSpacing: "0.04em" }}>You have</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {analyzeModal.matchedSkills.map((s: string) => (
                    <span key={s} style={{ background: "#dcfce7", color: "#166534", padding: "3px 10px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 500 }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Skills */}
            {analyzeModal.missingSkills?.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h4 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.04em" }}>Gaps to address</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {analyzeModal.missingSkills.map((s: string) => (
                    <span key={s} style={{ background: "#fee2e2", color: "#991b1b", padding: "3px 10px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 500 }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* All Job Skills */}
            {analyzeModal.skills?.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h4 style={{ margin: "0 0 8px", fontSize: "0.85rem", color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>Skills required by job</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {analyzeModal.skills.map((s: string) => (
                    <span key={s} style={{ background: "#f1f5f9", color: "#334155", padding: "3px 10px", borderRadius: "20px", fontSize: "0.8rem" }}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Tools / Experience / Industry */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
              {analyzeModal.experienceRequired && (
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Experience</div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1e293b" }}>{analyzeModal.experienceRequired}</div>
                </div>
              )}
              {analyzeModal.seniority && (
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Seniority</div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1e293b", textTransform: "capitalize" }}>{analyzeModal.seniority}</div>
                </div>
              )}
              {analyzeModal.companyIndustry && (
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Industry</div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1e293b" }}>{analyzeModal.companyIndustry}</div>
                </div>
              )}
              {analyzeModal.remote !== undefined && (
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "4px" }}>Work type</div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1e293b" }}>{analyzeModal.remote ? "Remote" : "On-site / Hybrid"}</div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
              <button className="hb-btn hb-btn-secondary" onClick={() => setAnalyzeModal(null)}>Close</button>
              <button
                className="hb-btn hb-btn-primary"
                onClick={handleApplyInsights}
                disabled={applyingInsights}
              >
                {applyingInsights ? "Saving..." : "Save Insights to Record"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Tracker Modal */}
      {showProgressTracker && trackerApp && ["interview_scheduled", "accepted", "rejected"].includes(trackerApp.status) && (
        <ProgressTrackerLinks
          applicationId={trackerApp.id}
          status={trackerApp.status as "interview_scheduled" | "accepted" | "rejected"}
          jobTitle={trackerApp.jobTitle}
          company={trackerApp.company}
          onClose={() => setShowProgressTracker(false)}
          isPremium={isPremium}
          updatedVia={trackerApp.updatedVia as "manual" | "email-sync" | undefined}
        />
      )}
    </div>
  );
};

export default JobApplications;
